import { useStoreState } from '@ariakit/react';
import { Disclosure, DisclosureContent, useDisclosureStore } from '@ariakit/react/disclosure';
import cn from 'classnames';
import { Children, isValidElement, useEffect, useEffectEvent, useMemo } from 'react';
import { Spindown } from '#components/atoms/spindown/Spindown.tsx';
import { useAppLocation } from '#hooks/useAppLocation.ts';
import styles from './NavGroup.module.css';

type Props = {
  title: string;
  isOpen: boolean;
  setOpen: (shouldOpen: boolean) => void;
  isActive: boolean;
  setActive: (active: boolean) => void;
  children: React.ReactNode;
  className?: string;
};

export function NavGroup({ children, className, isOpen, setOpen, isActive, setActive, title }: Props) {
  const { pathname } = useAppLocation();

  const disclosure = useDisclosureStore({
    animated: !__IS_TESTING__,
    open: isOpen,
    setOpen: handleOpenChange,
  });
  const animating = useStoreState(disclosure, 'animating');
  function handleOpenChange(open: boolean) {
    // While animating, "open" is still in the old state, so we have to wait
    if (!__IS_TESTING__ && !animating && open !== isOpen) {
      setOpen(open);
    } else if (__IS_TESTING__) {
      setOpen(open);
    }
  }

  // Check the list of "to" props of sub-nav items, so we can be sure to treat this as active when one of those are visited
  const hasMatchingChildren = useMemo(() => {
    const targets = Children.toArray(children).map(
      (child) => (isValidElement<{ to: string }>(child) && child.props.to) || ''
    );

    return targets.some((target) => pathname.startsWith(target));
  }, [children, pathname]);

  useEffect(() => {
    if (hasMatchingChildren) {
      // Show as active when the user is on a sub-nav item's page
      setActive(true);
    } else {
      setActive(false);
    }
  }, [hasMatchingChildren, setActive]);

  const setOpenEffectEvent = useEffectEvent(setOpen);
  useEffect(() => {
    if (hasMatchingChildren) {
      setOpenEffectEvent(true);
    } else {
      setOpenEffectEvent(false);
    }
  }, [hasMatchingChildren]);

  const classes = cn(styles.NavGroup, className, { [styles.NavGroup___active]: isActive });

  const subItems = Children.map(children, (child) => {
    if (!isValidElement<{ to: string }>(child)) {
      return child;
    }

    return <li key={child.props.to}>{child}</li>;
  });

  if (!subItems || subItems.length === 0) {
    throw new Error('Must provide one or more children to NavGroup');
  }

  if (subItems.length > 6) {
    throw new Error('Must update NavGroup styles to support more than 6 items');
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const menuItemsLengthClass = (styles as any)[`NavGroup_menu___items${subItems.length}`] as string;

  return (
    <>
      <Disclosure store={disclosure} className={classes}>
        {title}
        <Spindown className={styles.NavGroup_arrow} isExpanded={isOpen} isMobile={true} />
      </Disclosure>

      <DisclosureContent store={disclosure} className={cn(styles.NavGroup_menu, menuItemsLengthClass)}>
        <ul className={styles.NavGroup_subitems}>{subItems}</ul>
      </DisclosureContent>
    </>
  );
}
